import {
  AudioLines,
  Captions,
  Combine,
  Crop,
  Film,
  Gauge,
  Image,
  ImageDown,
  Maximize2,
  Mic2,
  Paintbrush,
  PictureInPicture2,
  Repeat2,
  RotateCw,
  Scissors,
  Shrink,
  Sparkles,
  TimerReset,
  Volume2,
  VolumeX,
} from 'lucide-react'
import type { ToolDefinition, ToolParams } from './types'

export const tools: ToolDefinition[] = [
  {
    id: 'video-compress',
    name: '压缩视频',
    shortName: '压缩',
    description: '降低视频体积，在清晰度和文件大小之间取得平衡。',
    kind: 'video',
    icon: Shrink,
    support: 'standard',
    accept: 'video/*,.mkv,.mov',
    controls: [
      {
        key: 'quality',
        label: '压缩强度',
        type: 'select',
        defaultValue: 'balanced',
        options: [
          { label: '轻量 · 更清晰', value: 'high' },
          { label: '均衡 · 推荐', value: 'balanced' },
          { label: '强力 · 更小', value: 'small' },
        ],
      },
      {
        key: 'maxWidth',
        label: '最长边',
        type: 'select',
        defaultValue: '1920',
        options: [
          { label: '原尺寸', value: 'original' },
          { label: '1080p', value: '1920' },
          { label: '720p', value: '1280' },
          { label: '480p', value: '854' },
        ],
      },
    ],
    outputHint: '输出 MP4（H.264 / AAC）',
    limitation: '目标体积是估算值；浏览器内单遍压缩无法保证精确到指定 MB。',
  },
  {
    id: 'video-convert',
    name: '转换视频',
    shortName: '转换',
    description: '在常用视频容器之间转换，兼顾兼容性与体积。',
    kind: 'video',
    icon: Repeat2,
    support: 'standard',
    accept: 'video/*,.mkv,.avi,.flv',
    controls: [
      {
        key: 'format',
        label: '输出格式',
        type: 'select',
        defaultValue: 'mp4',
        options: [
          { label: 'MP4 · 兼容性优先', value: 'mp4' },
          { label: 'WebM · 网页优先', value: 'webm' },
          { label: 'MOV · 剪辑优先', value: 'mov' },
        ],
      },
    ],
    outputHint: '将按所选格式重新编码',
    limitation: 'AVI、WMV、FLV、HEVC、AC3 等旧格式能否读取取决于随应用提供的 FFmpeg 构建。',
  },
  {
    id: 'video-trim',
    name: '裁剪视频时长',
    shortName: '裁剪时长',
    description: '精确设置起点和终点，导出需要的片段。',
    kind: 'video',
    icon: Scissors,
    support: 'standard',
    accept: 'video/*,.mkv,.mov',
    controls: [
      {
        key: 'trimMode',
        label: '处理模式',
        type: 'segmented',
        defaultValue: 'precise',
        options: [
          { label: '快速', value: 'quick' },
          { label: '逐帧精确', value: 'precise' },
        ],
        hint: '快速模式对齐关键帧且不重编码；逐帧精确会重新编码。',
      },
      {
        key: 'keepAudio',
        label: '保留音频',
        type: 'segmented',
        defaultValue: 'yes',
        options: [
          { label: '是', value: 'yes' },
          { label: '否', value: 'no' },
        ],
      },
      { key: 'start', label: '开始时间', type: 'number', defaultValue: 0, min: 0, step: 0.01, unit: '秒' },
      { key: 'end', label: '结束时间', type: 'number', defaultValue: 10, min: 0.01, step: 0.01, unit: '秒' },
    ],
    outputHint: '时间轴可拖动；精确模式重新编码',
    limitation: '快速模式从最近关键帧开始，位置可能有轻微偏差；需要准确画面边界时请选择逐帧精确。',
  },
  {
    id: 'video-speed',
    name: '调整视频速度',
    shortName: '视频变速',
    description: '改变画面和声音速度，支持 0.5× 到 2×。',
    kind: 'video',
    icon: Gauge,
    support: 'standard',
    accept: 'video/*,.mkv,.mov',
    controls: [
      { key: 'speed', label: '播放速度', type: 'range', defaultValue: 1.25, min: 0.5, max: 2, step: 0.05, unit: '×' },
    ],
    outputHint: '画面和声音将同步变速',
    limitation: '本地处理采用 FFmpeg atempo；极端变速可能出现轻微音质损失。',
  },
  {
    id: 'video-crop',
    name: '裁切视频画面',
    shortName: '裁切画面',
    description: '按像素设置裁切区域，去掉画面边缘。',
    kind: 'video',
    icon: Crop,
    support: 'standard',
    accept: 'video/*,.mkv,.mov',
    controls: [
      { key: 'width', label: '宽度', type: 'number', defaultValue: 1280, min: 2, step: 2, unit: 'px' },
      { key: 'height', label: '高度', type: 'number', defaultValue: 720, min: 2, step: 2, unit: 'px' },
      { key: 'x', label: '左侧偏移', type: 'number', defaultValue: 0, min: 0, step: 2, unit: 'px' },
      { key: 'y', label: '顶部偏移', type: 'number', defaultValue: 0, min: 0, step: 2, unit: 'px' },
    ],
    outputHint: '超出原画面的区域会在处理前报错',
  },
  {
    id: 'video-resize',
    name: '调整视频尺寸',
    shortName: '缩放',
    description: '更改视频分辨率，可保持原始宽高比。',
    kind: 'video',
    icon: Maximize2,
    support: 'standard',
    accept: 'video/*,.mkv,.mov',
    controls: [
      { key: 'width', label: '目标宽度', type: 'number', defaultValue: 1280, min: 2, step: 2, unit: 'px' },
      {
        key: 'height',
        label: '目标高度',
        type: 'select',
        defaultValue: 'auto',
        options: [
          { label: '自动 · 保持比例', value: 'auto' },
          { label: '1080', value: '1080' },
          { label: '720', value: '720' },
          { label: '480', value: '480' },
        ],
      },
    ],
    outputHint: '自动高度会取最接近的偶数',
  },
  {
    id: 'video-merge',
    name: '合并视频',
    shortName: '合并',
    description: '按文件列表顺序，将多个视频连接成一个。',
    kind: 'video',
    icon: Combine,
    support: 'limited',
    accept: 'video/*,.mkv,.mov',
    multiple: true,
    minFiles: 2,
    filePrompt: '选择至少 2 个视频，列表顺序即合并顺序',
    controls: [],
    outputHint: '输出会统一为 MP4',
    limitation: '不同分辨率、帧率或方向的素材可能无法直接合并；建议先统一尺寸。',
  },
  {
    id: 'video-interpolate',
    name: '智能补帧',
    shortName: '补帧',
    description: '使用运动估计生成中间帧，让运动更顺滑。',
    kind: 'video',
    icon: Sparkles,
    support: 'limited',
    accept: 'video/*,.mkv,.mov',
    controls: [
      {
        key: 'interpolationMode',
        label: '插帧模式',
        type: 'segmented',
        defaultValue: 'blend',
        options: [
          { label: '快速平滑', value: 'blend' },
          { label: '运动补偿 · 极慢', value: 'mci', requiresMt: true },
        ],
        hint: '快速平滑可能在高速运动或切镜处出现重影；运动补偿仅多线程可用。',
      },
      {
        key: 'fps',
        label: '目标帧率',
        type: 'select',
        defaultValue: '60',
        options: [
          { label: '30 fps', value: '30' },
          { label: '60 fps', value: '60' },
        ],
      },
    ],
    outputHint: '默认使用 blend 快速平滑；运动补偿为多线程实验能力',
    limitation: '不是 AI。快速平滑可能有重影；MT blend 限 1080p/5 分钟，ST blend 限 720p/60 秒，MCI 限 MT 720p/30 秒。',
  },
  {
    id: 'video-mute',
    name: '视频静音',
    shortName: '静音',
    description: '移除音轨，不重新编码视频画面。',
    kind: 'video',
    icon: VolumeX,
    support: 'stable',
    accept: 'video/*,.mkv,.mov',
    controls: [],
    outputHint: '画面将尽可能无损复制',
  },
  {
    id: 'video-rotate',
    name: '旋转视频',
    shortName: '旋转',
    description: '旋转或镜像画面，直接写入像素避免播放器差异。',
    kind: 'video',
    icon: RotateCw,
    support: 'stable',
    accept: 'video/*,.mkv,.mov',
    controls: [
      {
        key: 'direction',
        label: '变换方式',
        type: 'select',
        defaultValue: 'clockwise',
        options: [
          { label: '顺时针 90°', value: 'clockwise' },
          { label: '逆时针 90°', value: 'counterclockwise' },
          { label: '旋转 180°', value: '180' },
          { label: '水平镜像', value: 'hflip' },
          { label: '垂直镜像', value: 'vflip' },
        ],
      },
    ],
    outputHint: '旋转会重新编码画面',
  },
  {
    id: 'video-filter',
    name: '视频滤镜',
    shortName: '滤镜',
    description: '调整画面气质，提供一组克制的常用预设。',
    kind: 'video',
    icon: Paintbrush,
    support: 'standard',
    accept: 'video/*,.mkv,.mov',
    controls: [
      {
        key: 'filter',
        label: '滤镜',
        type: 'select',
        defaultValue: 'mono',
        options: [
          { label: '黑白', value: 'mono' },
          { label: '暖色', value: 'warm' },
          { label: '冷色', value: 'cool' },
          { label: '高对比', value: 'contrast' },
          { label: '柔和', value: 'soft' },
        ],
      },
    ],
    outputHint: '滤镜将应用到完整视频',
  },
  {
    id: 'video-screenshot',
    name: '视频截图',
    shortName: '截图',
    description: '从指定时间点提取一张原分辨率 PNG 图片。',
    kind: 'video',
    icon: ImageDown,
    support: 'stable',
    accept: 'video/*,.mkv,.mov',
    controls: [
      { key: 'time', label: '截图时间', type: 'number', defaultValue: 1, min: 0, step: 0.1, unit: '秒' },
    ],
    outputHint: '输出 PNG 图片',
  },
  {
    id: 'video-reverse',
    name: '倒放视频',
    shortName: '倒放',
    description: '将画面与声音反向排列，生成倒放效果。',
    kind: 'video',
    icon: TimerReset,
    support: 'limited',
    accept: 'video/*,.mkv,.mov',
    controls: [],
    outputHint: '画面和音频都将倒放',
    limitation: '倒放需要缓存解码帧，内存占用很高。建议只处理 10 分钟以内、1080p 以下素材。',
  },
  {
    id: 'video-watermark',
    name: '添加水印',
    shortName: '水印',
    description: '把 PNG 或 JPG 图片叠加到视频角落。',
    kind: 'video',
    icon: PictureInPicture2,
    support: 'standard',
    accept: 'video/*,image/png,image/jpeg,.mkv,.mov',
    multiple: true,
    minFiles: 2,
    filePrompt: '先选视频，再选一张 PNG/JPG 水印图片',
    controls: [
      {
        key: 'position',
        label: '水印位置',
        type: 'select',
        defaultValue: 'bottom-right',
        options: [
          { label: '右下角', value: 'bottom-right' },
          { label: '左下角', value: 'bottom-left' },
          { label: '右上角', value: 'top-right' },
          { label: '左上角', value: 'top-left' },
          { label: '居中', value: 'center' },
        ],
      },
      { key: 'scale', label: '水印宽度', type: 'range', defaultValue: 20, min: 5, max: 50, step: 1, unit: '%' },
    ],
    outputHint: '水印透明度沿用图片自身设置',
  },
  {
    id: 'video-subtitles',
    name: '添加字幕',
    shortName: '字幕',
    description: '把 SRT 字幕烧录进画面，所有播放器都能看到。',
    kind: 'video',
    icon: Captions,
    support: 'limited',
    accept: 'video/*,.srt,.mkv,.mov',
    multiple: true,
    minFiles: 2,
    filePrompt: '先选视频，再选 UTF-8 编码的 SRT 字幕',
    controls: [],
    outputHint: '字幕将永久写入画面',
    limitation: '字幕依赖当前 FFmpeg 构建中的 libass；若浏览器报告滤镜不可用，请改用桌面版 FFmpeg。',
  },
  {
    id: 'audio-extract',
    name: '从视频提取音频',
    shortName: '提取音频',
    description: '移除画面，只保留声音并转换为常用格式。',
    kind: 'audio',
    icon: Mic2,
    support: 'stable',
    accept: 'video/*,.mkv,.mov',
    controls: [
      {
        key: 'format',
        label: '输出格式',
        type: 'select',
        defaultValue: 'mp3',
        options: [
          { label: 'MP3', value: 'mp3' },
          { label: 'WAV · 无压缩', value: 'wav' },
          { label: 'M4A · AAC', value: 'm4a' },
        ],
      },
    ],
    outputHint: '仅在本地解码音轨',
  },
  {
    id: 'audio-trim',
    name: '裁剪音频',
    shortName: '音频裁剪',
    description: '设置起点和终点，导出需要的声音片段。',
    kind: 'audio',
    icon: Scissors,
    support: 'standard',
    accept: 'audio/*,.flac,.m4a,.ogg',
    controls: [
      {
        key: 'trimMode',
        label: '裁剪模式',
        type: 'segmented',
        defaultValue: 'quick',
        options: [
          { label: '快速无损', value: 'quick' },
          { label: '精确保真', value: 'precise' },
        ],
        hint: '快速无损按编码帧对齐；精确模式会解码，WAV 保持 PCM 位深，其余默认输出 FLAC。',
      },
      { key: 'start', label: '开始时间', type: 'number', defaultValue: 0, min: 0, step: 0.1, unit: '秒' },
      { key: 'end', label: '结束时间', type: 'number', defaultValue: 10, min: 0.1, step: 0.1, unit: '秒' },
    ],
    outputHint: '快速模式跟随源格式并 stream-copy；精确模式默认无损',
    limitation: '快速无损的切点按压缩音频帧对齐，可能偏差一个 codec frame。',
  },
  {
    id: 'audio-convert',
    name: '转换音频',
    shortName: '音频转换',
    description: '在 MP3、WAV、AAC 与 FLAC 之间转换。',
    kind: 'audio',
    icon: AudioLines,
    support: 'standard',
    accept: 'audio/*,.flac,.m4a,.ogg',
    controls: [
      {
        key: 'format',
        label: '输出格式',
        type: 'select',
        defaultValue: 'mp3',
        options: [
          { label: 'MP3 · 通用', value: 'mp3' },
          { label: 'WAV · 无压缩', value: 'wav' },
          { label: 'M4A · AAC', value: 'm4a' },
          { label: 'FLAC · 无损压缩', value: 'flac' },
        ],
      },
    ],
    outputHint: '多声道素材转码时可能降为立体声',
  },
  {
    id: 'audio-merge',
    name: '合并音频',
    shortName: '音频合并',
    description: '按列表顺序连接多个音频文件。',
    kind: 'audio',
    icon: Combine,
    support: 'limited',
    accept: 'audio/*,.flac,.m4a,.ogg',
    multiple: true,
    minFiles: 2,
    filePrompt: '选择至少 2 个音频，列表顺序即合并顺序',
    controls: [],
    outputHint: '参数一致时无损 stream-copy，不一致时默认 FLAC',
    limitation: '声道布局不一致会在开始前阻止，不会静默降为立体声。',
  },
  {
    id: 'audio-speed',
    name: '调整音频速度',
    shortName: '音频变速',
    description: '改变节奏并尽量保持原始音调。',
    kind: 'audio',
    icon: Volume2,
    support: 'standard',
    accept: 'audio/*,.flac,.m4a,.ogg',
    controls: [
      { key: 'speed', label: '播放速度', type: 'range', defaultValue: 1.25, min: 0.5, max: 2, step: 0.05, unit: '×' },
    ],
    outputHint: '默认输出 FLAC，保留采样率与声道，速度范围 0.5×–2×',
    limitation: '变速必须解码；FLAC 避免新增有损代次，但不能恢复源文件已经丢失的信息。',
  },
]

export const toolMap = new Map(tools.map((tool) => [tool.id, tool]))

export function defaultsFor(tool: ToolDefinition): ToolParams {
  return Object.fromEntries(tool.controls.map((control) => [control.key, control.defaultValue]))
}

export const videoTools = tools.filter((tool) => tool.kind === 'video')
export const audioTools = tools.filter((tool) => tool.kind === 'audio')

export const supportCopy = {
  stable: { label: '轻量处理', note: '通常较快' },
  standard: { label: '标准处理', note: '需要重新编码' },
  limited: { label: '实验能力', note: '受格式或性能限制' },
} as const
